// src/components/Legal/PrivacyPolicy.js
import React from "react";
import { Container, Typography, Box, Link as MuiLink } from "@mui/material";
import { Helmet } from "react-helmet-async";

export default function PrivacyPolicy() {
  return (
    <>
    <Helmet>
      <title>Privacy Policy | FlyBnB</title>
      <meta name="description" content="Read FlyBnB's privacy policy to understand how we collect, use, and protect your personal information." />
      <link rel="canonical" href="https://flybnb.io/privacy" />
    </Helmet>
    <Container maxWidth="md" sx={{ my: 6 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        FlyBnB Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Last updated: May 1, 2025 {/* Ensure this date is correct */}
      </Typography>

      <Typography paragraph sx={{ mt: 3 }}>
        This Privacy Policy describes how Flybnb.io ("we," "us," or "our")
        collects, uses, and shares information about you when you use our
        website and services (the "Service").
      </Typography>

      <Box sx={{ mt: 4, "& > *": { mb: 3 } }}>
        {/* Section 1 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          1. Information We Collect
        </Typography>
        <Typography paragraph>
          We collect information to provide and improve our Service. This
          includes:
        </Typography>
        <Typography component="div" sx={{ mb: 1 }}>
          <strong>Information You Provide:</strong>
          <ul>
            <li>
              <Typography>
                <strong>Account Information:</strong> When you sign up for an
                account (including FlyBnB Pro), we collect information such as
                your email address. We may also collect your name and country if
                provided. Authentication is handled via Supabase.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Payment Information:</strong> For FlyBnB Pro
                subscriptions, payment processing is handled by our third-party
                provider, Stripe. We do not directly store your full payment
                card details. Stripe may provide us with transaction details
                (e.g., transaction ID, subscription status).
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Communications:</strong> If you contact us (e.g., via
                the contact form or email) or subscribe to our newsletter, we
                collect your name, email address, message content, and any other
                information you provide. Contact and subscription forms are
                processed via Formspree.
              </Typography>
            </li>
          </ul>
        </Typography>
        <Typography component="div">
          <strong>Information Collected Automatically:</strong>
          <ul>
            <li>
              <Typography>
                <strong>Search & Usage Data:</strong> We log details about your
                searches, including origin, destination, dates, passenger counts
                (adults, children), flight type, stay type, and selected filters
                (e.g., review scores, amenities).
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Log and Device Data:</strong> We automatically collect
                log data and device information when you access and use the
                Service. This may include your IP address, browser type,
                operating system, access times, pages viewed, and potentially
                your general location (country, city), mobile status, and proxy
                status (obtained via third-party IP lookup). We use this for
                service operation, security, analytics, and potentially for
                enforcing usage limits.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Cookies and Similar Technologies:</strong> We may use
                cookies and similar tracking technologies to operate and
                personalize the Service, remember your preferences, and analyze
                usage. See section 4 for more details.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Analytics & Error Tracking:</strong> We use Vercel
                Analytics for general website usage statistics and Sentry for
                error tracking and performance monitoring. These services may
                collect technical data about your device and interactions.
              </Typography>
            </li>
          </ul>
        </Typography>

        {/* Section 2 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          2. How We Use Your Information
        </Typography>
        <Typography paragraph>We use the information we collect to:</Typography>
        <Typography component="div">
          <ul>
            <li>
              <Typography>
                Provide, operate, maintain, and improve the Service.
              </Typography>
            </li>
            <li>
              <Typography>
                Process transactions (via Stripe) and manage subscriptions.
              </Typography>
            </li>
            <li>
              <Typography>
                Communicate with you, respond to inquiries, send service
                updates, and marketing communications (if you opt-in, like
                weekly deal alerts).
              </Typography>
            </li>
            <li>
              <Typography>Personalize your experience.</Typography>
            </li>
            <li>
              <Typography>
                Analyze usage patterns and trends to improve the Service.
              </Typography>
            </li>
            <li>
              <Typography>
                Monitor and prevent fraud, security incidents, and enforce our
                Terms of Service.
              </Typography>
            </li>
            <li>
              <Typography>Comply with legal obligations.</Typography>
            </li>
          </ul>
        </Typography>

        {/* Section 3 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          3. How We Share Your Information
        </Typography>
        <Typography paragraph>
          We do not sell your personal information. We may share information
          under the following circumstances:
        </Typography>
        <Typography component="div">
          <ul>
            <li>
              <Typography>
                <strong>With Service Providers:</strong> We share necessary
                information with third-party companies that help us operate,
                improve, support, and market our Service. These include:
                <ul>
                  <li>
                    <strong>Backend & Database Services:</strong> for
                    authentication, data storage, and core application
                    functionality.
                  </li>
                  <li>
                    <strong>Payment Processors:</strong> to securely handle
                    subscription payments and billing.
                  </li>
                  <li>
                    <strong>Data Aggregators & APIs:</strong> to retrieve
                    flight, accommodation, and related travel information based
                    on your searches.
                  </li>
                  <li>
                    <strong>Communication Services:</strong> to manage contact
                    forms, email subscriptions, and user communications.
                  </li>
                  <li>
                    <strong>Hosting & Analytics Providers:</strong> to host our
                    infrastructure and analyze usage.
                  </li>
                  <li>
                    <strong>Error Monitoring:</strong> to identify and fix
                    technical issues.
                  </li>
                </ul>
                These providers process information on our behalf under
                confidentiality agreements and with appropriate safeguards.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>For Legal Reasons:</strong> We may disclose information
                if required by law, subpoena, or other legal process, or if we
                believe in good faith that disclosure is necessary to protect
                our rights, protect your safety or the safety of others,
                investigate fraud, or respond to a government request.
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Business Transfers:</strong> If we are involved in a
                merger, acquisition, or sale of assets, your information may be
                transferred as part of that transaction.
              </Typography>
            </li>
          </ul>
        </Typography>

        {/* Section 4 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          4. Cookies and Tracking Technologies
        </Typography>
        <Typography paragraph>
          We use cookies and similar technologies (like localStorage) to help
          personalize your experience, remember preferences, and analyze site
          usage. Third-party services like Vercel Analytics and Sentry may also
          use cookies. You can typically control cookie settings through your
          browser.
        </Typography>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          4A. Microsoft Clarity Session Recording
        </Typography>
        <Typography paragraph>
          We partner with Microsoft Clarity and Microsoft Advertising to capture
          how you use and interact with our website through behavioral metrics,
          heatmaps, and session replay to improve and market our products and
          services. Website usage data is captured using first and third-party
          cookies and other tracking technologies to determine the popularity of
          products/services and online activity. Additionally, we use this
          information for site optimization, 3. How We Share Your Information
          fraud prevention, security purposes, and advertising. For more
          information about how Microsoft collects and uses your data, please
          see the{" "}
          <MuiLink
            href="https://privacy.microsoft.com/en-US/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft Privacy Statement
          </MuiLink>
          .
        </Typography>

        {/* Section 5 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          5. Data Security
        </Typography>
        <Typography paragraph>
          We implement reasonable security measures to protect your information.
          We rely on the security practices of our third-party providers
          (Supabase, Stripe, etc.). Your information is encrypted in transit and
          stored using secure cloud infrastructure. We follow industry best
          practices for authentication and access control. However, no internet
          transmission or electronic storage is 100% secure.
        </Typography>

        {/* Section 6 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          6. Data Retention
        </Typography>
        <Typography paragraph>
          We retain your personal information for as long as necessary to
          provide the Service, comply with our legal obligations, resolve
          disputes, and enforce our agreements (e.g., while your account is
          active). Search logs and analytics data may be kept for analytical
          purposes, potentially in an anonymized or aggregated form.
        </Typography>

        {/* Section 7 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          7. Your Rights
        </Typography>
        <Typography paragraph>
          Depending on your location, you may have certain rights regarding your
          personal information, such as the right to access, correct, or delete
          your data. To exercise these rights, please contact us at{" "}
          <MuiLink href="mailto:info@flybnb.io">
            <strong>info@flybnb.io</strong>
          </MuiLink>
          . You can usually manage your account information and subscription
          settings directly within your account (if applicable). You can
          unsubscribe from marketing emails using the link provided in the
          emails. Residents of the EU or California may have additional rights
          under GDPR or CCPA respectively.
        </Typography>

        {/* Section 8 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          8. International Data Transfers
        </Typography>
        <Typography paragraph>
          Your information may be transferred to, and maintained on, computers
          located outside of your state, province, country, or other
          governmental jurisdiction where the data protection laws may differ.
          We utilize services like Supabase, Stripe, Vercel, and Sentry which
          may store data globally, including in the United States. By using the
          Service, you consent to this transfer.
        </Typography>

        {/* Section 9 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          9. Children's Privacy
        </Typography>
        <Typography paragraph>
          The Service is not intended for individuals under the age of 18. We do
          not knowingly collect personal information from children under 18.
        </Typography>

        {/* Section 10 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          10. Changes to This Policy
        </Typography>
        <Typography paragraph>
          We may update this Privacy Policy from time to time. We will notify
          you of any significant changes by posting the new policy on the
          website at <strong>/privacy</strong> {/* Confirm this path */} and
          updating the "Last Updated" date. Your continued use of the Service
          after the changes constitutes your acceptance.
        </Typography>

        {/* Section 11 */}
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          11. Contact Us
        </Typography>
        <Typography paragraph>
          If you have any questions about this Privacy Policy or your data,
          please contact us at:{" "}
          <MuiLink href="mailto:info@flybnb.io">
            <strong>info@flybnb.io</strong>
          </MuiLink>
          . {/* Email from original file */}
        </Typography>
      </Box>
    </Container>
    </>
  );
}
